import { Suspense } from "react";
import { baseUrl } from "@kaltura/mediaspace-shared-utils";
import { KmsTypeHeroProps } from "@kaltura/mediaspace-shared-types";
import { MediaTitleActionsProps, MediaTitleArea, MediaTitleAreaProps } from "./MediaTitleArea";
import { TitleSkeleton } from "./title-skeleton/TitleSkeleton";
import { ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { MediaPageComponentExtraProps } from "../MediaPageComponentExtraProps";
import { useFetchKmsData } from "@kaltura/mediaspace-shared-hooks";

/**
 * Area below the player (or other media hero component) that contains:
 * - media title
 * - basic info (e.g. creation date)
 * - buttons (e.g. likes, comments)
 * - actions menu (e.g. edit, delete, publish)
 */

interface MediaTitleAreaContentProps
    extends Pick<MediaTitleAreaProps, "titleAlerts" | "isEmptyRightPanel" | "onRequestEntryReload"> {
    fetchData: () => MediaTitleActionsProps;
}

/**
 * Media title area
 */
const MediaTitleAreaContent = ({
    fetchData,
    titleAlerts = [],
    isEmptyRightPanel,
    onRequestEntryReload,
}: MediaTitleAreaContentProps) => {
    const mediaTitleActionsProps = fetchData();

    return (
        <MediaTitleArea
            titleAlerts={titleAlerts}
            isEmptyRightPanel={isEmptyRightPanel}
            mediaTitleActionsProps={mediaTitleActionsProps}
            onRequestEntryReload={onRequestEntryReload}
        />
    );
};

export interface MediaTitleAreaContainerProps
    extends Pick<KmsTypeHeroProps, "categoryId" | "playlist" | "titleAlerts">,
        MediaPageComponentExtraProps {
    entryId: string;
    isEmptyRightPanel: boolean;
    reloadCounter?: number;
    isReadOnly?: boolean;
}

/**
 * Media title area Suspense Container
 */
export const MediaTitleAreaContainer = ({
    entryId,
    categoryId,
    playlist,
    titleAlerts,
    isEmptyRightPanel = false,
    reloadCounter = 0,
    onRequestEntryReload,
    isReadOnly = false,
}: MediaTitleAreaContainerProps) => {
    const url =
        baseUrl
        + "/default/media/entry-actions/id/"
        + entryId
        + (categoryId ? "/categoryid/" + categoryId : "")
        + (playlist ? "/playlistid/" + playlist.id : "");

    const fetchData = useFetchKmsData<MediaTitleActionsProps>({ url, data: { isReadOnly } }, [url, reloadCounter]);

    return (
        <ErrorBoundary fallback={<TitleSkeleton />}>
            <Suspense fallback={<TitleSkeleton />}>
                <MediaTitleAreaContent
                    fetchData={fetchData}
                    titleAlerts={titleAlerts}
                    isEmptyRightPanel={isEmptyRightPanel}
                    onRequestEntryReload={onRequestEntryReload}
                />
            </Suspense>
        </ErrorBoundary>
    );
};
